import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, PhoneCall, Sparkles } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import MvpHeader from '@/components/layout/MvpHeader.tsx';
import EmailVerificationBanner from '@/components/shared/EmailVerificationBanner.tsx';
import { useAuth } from '@/context/AuthContext.tsx';
import { setPendingFlow, type MvpFlow } from '@/lib/mvpFlow';

export default function MvpHomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, isSuperUser, user } = useAuth();

  // MVP: el usuario normal queda ligado al primer flujo que elige (persistido
  // en backend). Una vez fijado, la home sólo muestra su tarjeta.
  const chosenFlow = isAuthenticated && !isSuperUser ? (user?.chosenFlow ?? null) : null;

  // Con sesión, FlowLock persiste la elección al entrar al flujo. Sin sesión,
  // la elección queda pendiente en localStorage y el destino viaja en
  // location.state.next para que Login/Register devuelvan al usuario allí.
  const go = (flow: MvpFlow) => {
    const target = `/${flow}`;
    if (isAuthenticated) {
      navigate(target);
    } else {
      setPendingFlow(flow);
      navigate('/login', { state: { next: target } });
    }
  };

  const evaluateCard = (
    <FlowCard
      icon={PhoneCall}
      title={t('mvpHome.evaluateTitle')}
      description={t('mvpHome.evaluateDesc')}
      cta={t('mvpHome.start')}
      onClick={() => go('evaluate')}
    />
  );

  const pitchCard = (
    <FlowCard
      icon={Sparkles}
      title={t('mvpHome.pitchTitle')}
      badge={t('mvpHome.pitchBadge')}
      description={t('mvpHome.pitchDesc')}
      cta={t('mvpHome.start')}
      onClick={() => go('pitch')}
    />
  );

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Cabecera: misma barra que login/registro (logo izq.; idioma + tema dcha.) */}
      <MvpHeader />

      <div className="px-4 sm:px-6">
        <EmailVerificationBanner />
      </div>

      {/* Dos botones grandes centrados (o sólo el flujo elegido para el usuario normal) */}
      <main className="flex flex-1 items-center justify-center px-4 pb-20 sm:px-6">
        <div className="w-full max-w-4xl animate-fade-in">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <h1
              className="font-display font-[800] text-foreground"
              style={{ fontSize: 'clamp(28px, 4.5vw, 44px)', lineHeight: 1.15, letterSpacing: '-0.5px' }}
            >
              {t('mvpHome.title')}
            </h1>
            <p className="mt-4 text-base text-muted-foreground sm:text-lg">
              {t('mvpHome.subtitle')}
            </p>
          </div>

          {chosenFlow ? (
            <div className="mx-auto max-w-md">
              {chosenFlow === 'evaluate' ? evaluateCard : pitchCard}
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {evaluateCard}
              {pitchCard}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

interface FlowCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  cta: string;
  onClick: () => void;
  badge?: string;
}

function FlowCard({ icon: Icon, title, description, cta, onClick, badge }: FlowCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full flex-col items-start gap-4 rounded-2xl border border-border bg-card p-8 text-left shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-primary/50 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:p-10"
    >
      <div
        className="flex h-14 w-14 items-center justify-center rounded-2xl text-white transition-transform duration-200 group-hover:scale-110"
        style={{ background: 'var(--gradient-agencium)' }}
      >
        <Icon className="size-7" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-xl font-bold text-foreground sm:text-2xl">{title}</h2>
        {badge && (
          <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
            {badge}
          </span>
        )}
      </div>

      <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>

      <span className="mt-auto inline-flex items-center gap-1 pt-2 text-sm font-semibold text-primary">
        {cta}
        <ChevronRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
      </span>
    </button>
  );
}
